import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button, Field, Input, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { allocationPushOutcome, type XeroOutcomeLine } from '@/lib/credit-notes';
import { sumDecimalStrings } from '@/lib/decimal';
import { formatDecimalString } from '@/lib/format';
import type { CreditNote, Invoice } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * "Apply credit balance" — owner's decision 2026-10-12. Shown on an invoice's
 * page and in its edit modal header, ONLY when the invoice's company holds
 * spendable credit in the invoice's own currency.
 *
 * It opens a dialog listing those credit notes, each pre-filled with the most
 * it could apply (its credit left, capped at the invoice total). The amounts
 * are editable. Each apply is an ordinary `POST /credit-notes/:id/allocations`,
 * so it reaches Xero exactly as applying from the credit note page does.
 *
 * ⚠️ The invoice's own outstanding balance is NOT on the invoice read, so the
 * pre-fill caps at the grand total and the server's refusal states the real
 * limit. A Xero-draft invoice keeps the credit here and says why it did not
 * reach Xero — EMERGE never approves an invoice in Xero to make room for credit.
 */

/** a − b as a decimal string, or null if either is not a figure. */
function minus(a: string, b: string): string | null {
  const negated = b.trim().startsWith('-') ? b.trim().slice(1) : `-${b.trim()}`;
  return sumDecimalStrings([a.trim(), negated]);
}

function isPositive(value: string): boolean {
  const normalised = sumDecimalStrings([value.trim()]);
  return normalised !== null && !normalised.startsWith('-') && /[1-9]/.test(normalised);
}

function isZeroOrBlank(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  const normalised = sumDecimalStrings([trimmed]);
  return normalised !== null && !/[1-9]/.test(normalised);
}

/** The smaller of two non-negative decimal strings. */
function smaller(a: string, b: string): string {
  const diff = minus(a, b);
  return diff !== null && diff.startsWith('-') ? a : b;
}

function money(amount: string, currency: string): string {
  return `${formatDecimalString(amount)} ${currency}`;
}

function noteLabel(note: CreditNote): string {
  return note.xeroCreditNoteNumber?.trim() || note.subject.trim() || `Credit note #${note.id}`;
}

interface Props {
  invoice: Invoice;
  /** Called after any credit was applied, so the caller re-reads the invoice. */
  onApplied?: () => void;
  size?: 'sm' | 'md';
}

export function ApplyCreditButton({ invoice, onApplied, size = 'md' }: Props) {
  const { ability } = useAuth();
  const canApply = ability.canUpdate('credit-notes');
  const companyId = invoice.company?.id ?? null;
  const currency = invoice.currency;

  const [notes, setNotes] = useState<CreditNote[] | null>(null);
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!canApply || companyId === null || !currency) return;
    const controller = new AbortController();
    api
      .listCreditNotes(
        // Oldest first: the server's default order is newest first.
        {
          companyId,
          currency,
          spendable: true,
          hasBalance: true,
          perPage: 100,
          sort: 'id',
          order: 'asc',
        },
        controller.signal,
      )
      .then((page) => {
        if (!controller.signal.aborted) setNotes(page.data);
      })
      .catch(() => {
        // No button on a failed read: it would offer credit we could not confirm.
        if (!controller.signal.aborted) setNotes([]);
      });
    return () => controller.abort();
  }, [canApply, companyId, currency, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  if (!canApply || !currency) return null;
  const hasCredit = notes !== null && notes.length > 0;
  // The dialog stays mounted after the credit is used up, so its outcome lines survive the re-read.
  if (!hasCredit && !open) return null;

  const total = sumDecimalStrings((notes ?? []).map((note) => note.remainingAmount)) ?? '0';

  return (
    <>
      {hasCredit && (
        <Button variant="secondary" size={size} onClick={() => setOpen(true)}>
          Apply credit balance ({money(total, currency)})
        </Button>
      )}
      {open && notes !== null && (
        <ApplyCreditDialog
          invoice={invoice}
          currency={currency}
          notes={notes}
          onClose={() => setOpen(false)}
          onApplied={() => {
            refresh();
            onApplied?.();
          }}
        />
      )}
    </>
  );
}

function ApplyCreditDialog({
  invoice,
  currency,
  notes: initialNotes,
  onClose,
  onApplied,
}: {
  invoice: Invoice;
  currency: string;
  notes: CreditNote[];
  onClose: () => void;
  onApplied: () => void;
}) {
  // A snapshot: the button re-reads after an apply, and the list may shrink or empty under the dialog.
  const [notes] = useState(initialNotes);
  // Notes already applied in this dialog — disabled, and never re-sent by a second press.
  const [applied, setApplied] = useState<ReadonlySet<number>>(() => new Set());
  // Pre-fill oldest note first, never applying more in total than the invoice total.
  const [amounts, setAmounts] = useState<Record<number, string>>(() => {
    let left = invoice.grandTotal;
    const out: Record<number, string> = {};
    for (const note of notes) {
      const take = isPositive(left) ? smaller(note.remainingAmount, left) : '0.00';
      out[note.id] = isPositive(take) ? (sumDecimalStrings([take]) ?? take) : '';
      left = minus(left, take) ?? '0';
    }
    return out;
  });
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [outcomes, setOutcomes] = useState<{ label: string; line: XeroOutcomeLine }[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const refusals: Record<number, string> = {};
    const chosen = notes.filter(
      (note) => !applied.has(note.id) && !isZeroOrBlank(amounts[note.id] ?? ''),
    );
    for (const note of chosen) {
      const typed = (amounts[note.id] ?? '').trim();
      if (sumDecimalStrings([typed]) === null) refusals[note.id] = 'Enter an amount, like 250.00.';
      else if (!isPositive(typed)) refusals[note.id] = 'Enter an amount above zero.';
    }
    if (chosen.length === 0) {
      setErrors({});
      setOutcomes([{ label: '', line: { tone: 'danger', text: 'Enter an amount on at least one credit note.' } }]);
      return;
    }
    if (Object.keys(refusals).length > 0) {
      setErrors(refusals);
      return;
    }

    setSaving(true);
    setErrors({});
    setOutcomes([]);
    const results: { label: string; line: XeroOutcomeLine }[] = [];
    const failed: Record<number, string> = {};
    const nowApplied = new Set(applied);
    let anyApplied = false;
    // One note at a time, so the server's per-invoice limit is checked against each apply in turn.
    for (const note of chosen) {
      try {
        const response = await api.createCreditNoteAllocation(note.id, {
          invoiceId: invoice.id,
          amount: (amounts[note.id] ?? '').trim(),
        });
        anyApplied = true;
        nowApplied.add(note.id);
        results.push({
          label: noteLabel(note),
          line: response.xeroPush
            ? allocationPushOutcome(response.xeroPush, 'apply')
            : { tone: 'success', text: 'Applied.' },
        });
      } catch (error) {
        failed[note.id] =
          error instanceof ApiError
            ? (Object.values(error.fieldErrors ?? {})[0] ?? error.formMessage ?? error.message)
            : 'That could not be applied.';
      }
    }
    setSaving(false);
    setApplied(nowApplied);
    setErrors(failed);
    setOutcomes(results);
    if (anyApplied) {
      onApplied();
      if (Object.keys(failed).length === 0) setDone(true);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply credit balance"
      size="md"
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="apply-credit-form" loading={saving}>
              Apply credit
            </Button>
          </>
        )
      }
    >
      <form id="apply-credit-form" onSubmit={submit} className="flex flex-col gap-4">
        <p className="m-0 text-sm text-fg-secondary">
          To {invoice.xeroInvoiceNumber ?? invoice.subject} · total {money(invoice.grandTotal, currency)}
        </p>
        {notes.map((note) => (
          <Field
            key={note.id}
            label={`${noteLabel(note)} — ${money(note.remainingAmount, currency)} left`}
            error={errors[note.id]}
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={amounts[note.id] ?? ''}
                invalid={Boolean(errors[note.id])}
                disabled={saving || done || applied.has(note.id)}
                onChange={(event) =>
                  setAmounts((prev) => ({ ...prev, [note.id]: event.target.value }))
                }
              />
            )}
          </Field>
        ))}
        {outcomes.map((outcome, index) => (
          <Notice key={index} tone={outcome.line.tone === 'neutral' ? 'neutral' : outcome.line.tone}>
            {outcome.label ? `${outcome.label}: ` : ''}
            {outcome.line.text}
          </Notice>
        ))}
      </form>
    </Modal>
  );
}
